/*
  International visitor waitlist banner.

  Two things are deliberate here:

  1. The country comes from Shopify's own detection endpoint, not from Liquid.
     localization.country and Shopify.country both report the *market* country (US for every
     visitor on this store), so they cannot tell a Colombian visitor apart from a US one.
     /browsing_context_suggestions.json returns the geo-IP country in detected_values.

  2. The subscribe call goes straight to the Klaviyo public API rather than through
     window.klaviyo. On this theme version that global never becomes the real Klaviyo object
     (it stays an empty array, which is also why Boost Commerce throws isIdentified errors),
     so depending on it would leave this form silently broken.
*/
(function () {
  'use strict';

  var SUGGESTIONS_URL = '/browsing_context_suggestions.json';
  var COUNTRY_CACHE_KEY = '13l:detected-country';
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';

  function safeStorage(storage) {
    // Private windows and blocked site-data make these throw on access, not just on write.
    return {
      get: function (key) {
        try {
          return window[storage].getItem(key);
        } catch (error) {
          return null;
        }
      },
      set: function (key, value) {
        try {
          window[storage].setItem(key, value);
        } catch (error) {
          /* nothing to do: the banner just asks again next time */
        }
      }
    };
  }

  var localStore = safeStorage('localStorage');
  var sessionStore = safeStorage('sessionStorage');

  function parseCountries(value) {
    return (value || '')
      .split(',')
      .map(function (code) {
        return code.trim().toUpperCase();
      })
      .filter(Boolean);
  }

  function detectCountry() {
    var cached = sessionStore.get(COUNTRY_CACHE_KEY);

    if (cached) {
      return Promise.resolve(cached);
    }

    return fetch(SUGGESTIONS_URL, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var detected =
          data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;

        if (!detected) {
          return null;
        }

        var code = String(detected).toUpperCase();
        sessionStore.set(COUNTRY_CACHE_KEY, code);
        return code;
      })
      .catch(function () {
        // Never let a failed lookup show the banner to a US shopper.
        return null;
      });
  }

  function readStrings(banner) {
    var node = banner.querySelector('[data-waitlist-strings]');

    if (!node) {
      return {};
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function subscribe(banner, email, country) {
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                location: { country: country },
                properties: {
                  // The whole point of the ticket: which markets to open first.
                  'Waitlist Country': country,
                  'Waitlist Source': 'International waitlist banner'
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: banner.dataset.listId } }
        }
      }
    };

    return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(banner.dataset.companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Klaviyo responded ' + response.status);
      }

      return response;
    });
  }

  function bindForm(banner, country) {
    var form = banner.querySelector('[data-waitlist-form]');

    if (!form) {
      return;
    }

    var input = form.querySelector('[data-waitlist-email]');
    var submit = form.querySelector('[type="submit"]');
    var feedback = banner.querySelector('[data-waitlist-feedback]');
    var strings = readStrings(banner);

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      if (!input.value || !input.checkValidity()) {
        feedback.textContent = strings.invalid || '';
        input.focus();
        return;
      }

      submit.disabled = true;
      feedback.textContent = '';

      subscribe(banner, input.value.trim(), country)
        .then(function () {
          form.hidden = true;
          feedback.textContent = strings.success || '';
          feedback.classList.add('WaitlistBanner__Feedback--success');
        })
        .catch(function () {
          submit.disabled = false;
          feedback.textContent = strings.error || '';
          feedback.classList.remove('WaitlistBanner__Feedback--success');
        });
    });
  }

  /*
    Klaviyo's teaser offers a first-order discount. Showing it to a visitor we cannot ship to
    dangles a code they can never redeem, and it also overlaps this banner. Klaviyo injects it
    late, so watch for it rather than querying once.

    The teaser must stay in the layout while hidden. Closing the Klaviyo popup (the X or the
    decline button) hands off to the teaser and only releases the body scroll lock once the
    teaser has rendered. With display: none that hand-off never completes: the popup stays
    mounted and the page is left without scroll until reload. visibility keeps the teaser
    rendered but invisible and out of the accessibility tree; pointer-events keeps it from
    catching clicks.
  */
  function toggleKlaviyoTeaser(hide) {
    var teasers = document.querySelectorAll('[class*="kl-teaser"]');

    Array.prototype.forEach.call(teasers, function (teaser) {
      if (hide) {
        // Klaviyo rewrites its own inline style, so !important is what actually sticks.
        teaser.style.setProperty('visibility', 'hidden', 'important');
        teaser.style.setProperty('pointer-events', 'none', 'important');
      } else {
        teaser.style.removeProperty('visibility');
        teaser.style.removeProperty('pointer-events');
      }
    });
  }

  function suppressKlaviyoTeaser() {
    toggleKlaviyoTeaser(true);

    if (!window.MutationObserver) {
      return null;
    }

    var observer = new MutationObserver(function () {
      toggleKlaviyoTeaser(true);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }

  function reveal(banner, country) {
    banner.hidden = false;
    banner.classList.add('WaitlistBanner--visible');
    bindForm(banner, country);

    var observer = suppressKlaviyoTeaser();
    var close = banner.querySelector('[data-waitlist-close]');

    if (!close) {
      return;
    }

    close.addEventListener('click', function () {
      banner.hidden = true;
      localStore.set(banner.dataset.storageKey, '1');

      // Dismissing the banner gives the storefront its teaser back.
      if (observer) {
        observer.disconnect();
      }

      toggleKlaviyoTeaser(false);
    });
  }

  /*
    The delay is counted from the moment the country is known, not from page load: the
    detection round-trip is what it is, and starting the clock before it would make the
    configured number mean something different on a slow connection than on a fast one.
  */
  function setup(banner) {
    if (localStore.get(banner.dataset.storageKey)) {
      return;
    }

    var allowed = parseCountries(banner.dataset.allowedCountries);
    var delay = parseInt(banner.dataset.delay, 10);

    if (isNaN(delay) || delay < 0) {
      delay = 0;
    }

    detectCountry().then(function (country) {
      if (!country || allowed.indexOf(country) !== -1) {
        return;
      }

      if (delay === 0) {
        reveal(banner, country);
        return;
      }

      window.setTimeout(function () {
        reveal(banner, country);
      }, delay * 1000);
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-waitlist-banner]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
